import { useAppRouteParams } from "@/AppRoutes";
import React, { useMemo, ReactNode, createContext, useContext } from "react";
import {
  Organization,
  OrganizationRaw,
  organizationToFirestore,
  useOrganization,
} from "../../../domain/Organization";

const organizationCtx = createContext<{
  listeningOrganization: Organization | null;
  organization: OrganizationRaw | null;
  runReloadOrganization: () => Promise<void>;
  isLoadedOrganization: boolean;
}>({
  listeningOrganization: null,
  organization: null,
  runReloadOrganization: async () => {
    // nop
  },
  isLoadedOrganization: false,
});

/**
 * @description サイドメニュー, 組織設定画面などさまざまな場所で使う組織データを管理
 */
export const OrganizationProvider: React.FC<{
  children: ReactNode;
}> = ({ children }) => {
  const { organizationId } = useAppRouteParams();
  //TODO: 本質的にはlisteningOrganizationだけで十分だが修正範囲がとても広いため保留
  const [listeningOrganization, isLoadedOrganization] = useOrganization(
    organizationId,
    {}
  );

  const organizationCtxValue = useMemo(
    () => ({
      listeningOrganization,
      organization: listeningOrganization
        ? organizationToFirestore(listeningOrganization)
        : null,
      runReloadOrganization: async () => {
        // TODO: Remove unused calls
      },
      isLoadedOrganization,
    }),
    [listeningOrganization, isLoadedOrganization]
  );

  return (
    <organizationCtx.Provider value={organizationCtxValue}>
      {children}
    </organizationCtx.Provider>
  );
};

export const useCachedOrganization = () => {
  return useContext(organizationCtx);
};

/**
 * 組織が TADS の利用を開始しているか。
 *
 * 判定は組織ドキュメントの `is_tads_enabled` で、api-server が LDS のテーブル作成に
 * 成功したあとに立てる。`GET /admin/tads_table` は aa:developer 以上しか叩けないので、
 * 一般ユーザーから見える導線の出し分けはこちらを使う。
 *
 * 閉じるのは「TADS を使い始める」導線 (本体の管理画面・シート作成の種別) だけにすること。
 * 既に `tadReferTo` を持つシートの表示や編集まで閉じると、データがあるのに触れなくなる。
 */
export const useIsTadsEnabled = () => {
  const { listeningOrganization, isLoadedOrganization } =
    useCachedOrganization();
  return {
    isTadsEnabled: listeningOrganization?.isTadsEnabled ?? false,
    isLoadedTadsEnabled: isLoadedOrganization,
  };
};
